/** CIEDE2000 color difference. Identical colors return 0. */
export function deltaE2000(hexA: string, hexB: string): number {
  const [L1, a1, b1] = hexToLab(hexA);
  const [L2, a2, b2] = hexToLab(hexB);
  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const avgC = (C1 + C2) / 2;
  const g = 0.5 * (1 - Math.sqrt(avgC ** 7 / (avgC ** 7 + 25 ** 7)));
  const a1p = a1 * (1 + g);
  const a2p = a2 * (1 + g);
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);
  const avgCp = (C1p + C2p) / 2;
  const h1p = hue(a1p, b1);
  const h2p = hue(a2p, b2);
  let dhp = 0;
  if (C1p * C2p !== 0) {
    const dh = h2p - h1p;
    if (Math.abs(dh) <= 180) dhp = dh;
    else if (dh > 180) dhp = dh - 360;
    else dhp = dh + 360;
  }
  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp / 2) * (Math.PI / 180));
  const avgLp = (L1 + L2) / 2;
  let avgHp: number;
  if (C1p * C2p === 0) {
    avgHp = h1p + h2p;
  } else {
    const sum = h1p + h2p;
    const diff = Math.abs(h1p - h2p);
    if (diff <= 180) avgHp = sum / 2;
    else if (sum < 360) avgHp = (sum + 360) / 2;
    else avgHp = (sum - 360) / 2;
  }
  const T =
    1 -
    0.17 * Math.cos((avgHp - 30) * (Math.PI / 180)) +
    0.24 * Math.cos((2 * avgHp) * (Math.PI / 180)) +
    0.32 * Math.cos((3 * avgHp + 6) * (Math.PI / 180)) -
    0.2 * Math.cos((4 * avgHp - 63) * (Math.PI / 180));
  const dRo = 30 * Math.exp(-(((avgHp - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(avgCp ** 7 / (avgCp ** 7 + 25 ** 7));
  const Sl = 1 + (0.015 * (avgLp - 50) ** 2) / Math.sqrt(20 + (avgLp - 50) ** 2);
  const Sc = 1 + 0.045 * avgCp;
  const Sh = 1 + 0.015 * avgCp * T;
  const Rt = -Math.sin(2 * dRo * (Math.PI / 180)) * Rc;
  return Math.sqrt((dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh));
}

function hue(ap: number, b: number): number {
  if (ap === 0 && b === 0) return 0;
  const angle = (Math.atan2(b, ap) * 180) / Math.PI;
  return angle < 0 ? angle + 360 : angle;
}

function hexToLab(hex: string): [number, number, number] {
  const raw = hex.replace('#', '');
  const channels = [0, 2, 4].map((index) => {
    const value = parseInt(raw.slice(index, index + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  const [r, g, b] = channels;
  let x = r * 0.4124564 + g * 0.3575761 + b * 0.1804375;
  let y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  let z = r * 0.0193339 + g * 0.119192 + b * 0.9503041;
  x /= 0.95047;
  z /= 1.08883;
  const f = (t: number) => (t > 0.008856 ? t ** (1 / 3) : 7.787 * t + 16 / 116);
  const fx = f(x);
  const fy = f(y);
  const fz = f(z);
  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}
